/**
 * Pure parsers for pasted EmmyWatch / Ranfft caliber pages.
 *
 * Strict, whitelist-driven extraction:
 *  - We define a closed set of KNOWN_LABELS that appear on the spec block.
 *  - We scan the pasted text line-by-line, only accepting `Label: Value`
 *    pairs whose label is in the whitelist.
 *  - Site nav, search placeholders ("…i.e. Omega 561"), shipping banners,
 *    parts/balance-staff/related-movement sections — none of these contain
 *    `Known-Label: …` rows, so they're naturally ignored.
 *  - For EmmyWatch pages that show the title as `<Brand words> <caliber>`
 *    on one line followed by `(<short-brand>)` on the next, we detect that
 *    block explicitly and split the title into manufacturer + movement_id.
 *
 * Returns a partial `MovementIn` dict — fields we couldn't parse stay
 * absent so the UI can merge onto existing form state without clobbering
 * anything. Tested against real Emmy and Ranfft paste-buffers in
 * `__tests__/movementPaste.test.mjs`.
 */

// Whitelist of labels we'll accept. Sorted longest-first below so the
// alternation regex prefers the more specific label (e.g. "Lignes
// Dimensions" before "Lignes", "Movement size" before "Size").
const KNOWN_LABELS_RAW = [
  // identity
  "Manufacturer", "Manufacturers", "Brand", "Mark",
  "Calibre", "Caliber", "Movement number", "Movement",
  // sizing — Ranfft uses these compound labels with TAB separators
  "Lignes Dimensions", "Movement size",
  "Dimensions", "Diameter", "Size", "Ligne", "Lignes",
  "Height", "Thickness",
  "Further Measures",
  // mechanics
  "Jewels", "Stones", "Jewel count",
  "Frequency", "Vibrations", "Beat", "VPH", "vph", "bph", "A/h",
  "Lift angle", "Lift",
  "Power Reserve", "Power reserve", "Reserve",
  "Type", "Movement type",
  "Shock protection", "Shock system", "Antichoc", "Protection", "Shock",
  // dates
  "Period used", "Year introduced", "Introduced", "Production start",
  "Launch year", "Launched", "Since", "Year",
  "Discontinued", "Production end", "Until",
  // hands
  "Hand sizes", "Hands", "Hour hand", "Minute hand", "Second hand",
  // misc
  "Source", "URL",
];
// Longest first so e.g. "Lignes Dimensions" beats "Lignes" in the regex.
const KNOWN_LABELS = [...KNOWN_LABELS_RAW].sort((a, b) => b.length - a.length);

// Build alternation pattern, escaping spaces to allow flexible whitespace.
const LABEL_ALT = KNOWN_LABELS
  .map((l) => l.replace(/[.*+?^${}()|[\]\\]/g, "\\$&").replace(/ /g, "\\s+"))
  .join("|");

// Split a single line into multiple `Label: Value` segments when separated
// by 2+ spaces or tabs followed by another known label (handles 2-column
// tables that flatten to one line on copy).
const SPLIT_RE = new RegExp(`(?<=\\S)\\s{2,}(?=(?:${LABEL_ALT})\\s*:)`, "gi");
// Match a single `Label: Value` segment. Accept either a colon-style
// separator OR a single TAB (Ranfft layout) OR two-or-more whitespace
// characters (Emmy/other tables that flatten with double spaces).
const SEGMENT_RE = new RegExp(`^(${LABEL_ALT})(?:\\s*[:\\-–—]\\s*|\\t+\\s*|\\s{2,})(.+)$`, "i");

const MOVEMENT_TYPE_KEYWORDS = [
  [/\bautomatic\b|\bself[- ]?winding\b/i, "Automatic"],
  [/\bmanual(?:[ -]?wind(?:ing)?)?\b|\bhand[- ]?wind\b/i, "Manual"],
  [/\bquartz\b/i, "Quartz"],
  [/\btuning[- ]?fork\b|\baccutron\b/i, "Tuning Fork"],
  [/\bkinetic\b|\bauto[- ]?quartz\b/i, "Kinetic"],
];

function toFloat(v) {
  if (v === null || v === undefined) return null;
  // Strip comma-as-thousands separator FIRST ("18,000 vph" → "18000 vph"),
  // THEN treat any remaining comma as a decimal point ("11,5'''" → "11.5'''").
  // Order matters: doing it the other way would corrupt thousand-grouped values.
  let s = String(v).replace(/(\d),(\d{3})(?!\d)/g, "$1$2").replace(/,/g, ".");
  const m = s.match(/-?\d+(?:\.\d+)?/);
  return m ? parseFloat(m[0]) : null;
}

function toInt(v) {
  const f = toFloat(v);
  return f === null ? null : Math.round(f);
}

/**
 * Strip obviously irrelevant sections so they don't pollute downstream
 * regexes (e.g. "Related Movements" lists more `Ligne:` rows for sibling
 * calibers we don't want to grab; the description paragraph mentions the
 * movement name in prose which can confuse fallbacks).
 */
function stripBoilerplate(text) {
  let body = text;
  // Drop everything from these sub-section headings onward (Emmy).
  // "Buy this movement" is the canonical end-of-spec marker on Emmy pages —
  // everything after is the prose description, related movements, parts
  // listings, etc. Cutting here is the single most reliable way to keep the
  // parser anchored to the spec block.
  const cutMarkers = [
    /\bBuy this movement\b/i,
    /\bBalance Staff Listing\b/i,
    /\bMainspring Listing\b/i,
    /\bParts Listing\b/i,
    /\bRelated Movements\b/i,
    /All watch database content/i,
    /\bAbout Us\b\s*\n\s*Mobile App\b/i,
  ];
  for (const re of cutMarkers) {
    const m = body.match(re);
    if (m) body = body.slice(0, m.index);
  }
  // Drop search placeholders that mention example calibers ("…i.e. Omega 561")
  body = body.replace(/Search\s+movements?\s+here[^\n]*/gi, "");
  body = body.replace(/Search\s+here[^\n]*/gi, "");
  body = body.replace(/Search\s+Movements?\b/gi, "");
  // Drop common nav/footer scraps
  body = body.replace(/FREE\s+SHIPPING[^\n]*/gi, "");
  body = body.replace(/Just a moment\.\.\./gi, "");
  return body.trim();
}

/**
 * Extract a {label: value} map from a body of text using the whitelist.
 * First-match wins per label, so the spec block (which appears before any
 * listings or descriptions) always takes precedence.
 */
function extractLabelMap(body) {
  const map = {};
  for (const rawLine of body.split(/\r?\n/)) {
    const line = rawLine.trim();
    if (!line) continue;
    const segments = line.split(SPLIT_RE);
    for (const seg of segments) {
      const m = seg.trim().match(SEGMENT_RE);
      if (!m) continue;
      const key = m[1].toLowerCase().replace(/\s+/g, " ");
      const val = m[2].trim();
      if (val && !(key in map)) map[key] = val;
    }
  }
  return map;
}

/**
 * EmmyWatch shows the caliber as
 *   Hermann Becker 1151
 *   (Becker)
 * with no explicit "Manufacturer:" / "Calibre:" labels. Detect that two-line
 * block by looking for a line whose successor is just `(<short-brand>)`.
 */
function detectEmmyTitle(body) {
  const lines = body.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
  for (let i = 0; i < lines.length - 1; i += 1) {
    const cur = lines[i];
    const next = lines[i + 1];
    const parens = next.match(/^\(([A-Za-z][A-Za-z0-9 .\-&/]{0,40})\)$/);
    if (!parens) continue;
    if (cur.length > 60) continue; // not a title — too long
    if (!/[A-Za-z]/.test(cur) || !/\d/.test(cur)) continue;
    const tokens = cur.split(/\s+/);
    // Caliber portion starts at first token containing a digit.
    const calIdx = tokens.findIndex((t) => /\d/.test(t));
    if (calIdx <= 0) {
      return { mvmnt_mfg: parens[1].trim(), movement_id: cur };
    }
    return {
      mvmnt_mfg: tokens.slice(0, calIdx).join(" "),
      movement_id: tokens.slice(calIdx).join(" "),
    };
  }
  return null;
}

/**
 * Main entry. Accepts raw pasted text, returns { parsed, matched, raw_preview }.
 */
export function parseMovementPaste(raw) {
  const text = (raw || "").trim();
  if (text.length < 10) return { parsed: {}, matched: [], raw_preview: text };

  const body = stripBoilerplate(text);
  const map = extractLabelMap(body);
  const result = {};

  // ---- Identity ----
  const mfg = map["manufacturer"] || map["manufacturers"] || map["brand"] || map["mark"];
  if (mfg) result.mvmnt_mfg = mfg.replace(/\s+/g, " ").slice(0, 80);

  const cal = map["calibre"] || map["caliber"] || map["movement number"] || map["movement"];
  if (cal) result.movement_id = cal.replace(/\s+/g, " ").slice(0, 60);

  if (!result.mvmnt_mfg || !result.movement_id) {
    const title = detectEmmyTitle(body);
    if (title) {
      if (!result.mvmnt_mfg && title.mvmnt_mfg) result.mvmnt_mfg = title.mvmnt_mfg;
      if (!result.movement_id && title.movement_id) result.movement_id = title.movement_id;
    }
  }

  // If the caliber captured a manufacturer-prefixed value (e.g. "Hermann
  // Becker 1151" pasted as a single `Movement: …` line) split the brand off
  // by finding the first token that contains a digit.
  if (result.movement_id && !result.mvmnt_mfg) {
    const tokens = result.movement_id.split(/\s+/);
    const calIdx = tokens.findIndex((t) => /\d/.test(t));
    if (calIdx > 0 && calIdx < tokens.length) {
      result.mvmnt_mfg = tokens.slice(0, calIdx).join(" ");
      result.movement_id = tokens.slice(calIdx).join(" ");
    }
  }

  // ---- Movement type ----
  const typeStr = map["type"] || map["movement type"];
  if (typeStr) {
    for (const [re, type] of MOVEMENT_TYPE_KEYWORDS) {
      if (re.test(typeStr)) { result.movement_type = type; break; }
    }
  }
  if (!result.movement_type) {
    // Fall back to keyword scanning across the body — but constrained to a
    // small spec window so descriptions don't poison the result.
    const window = body.slice(0, 1200);
    for (const [re, type] of MOVEMENT_TYPE_KEYWORDS) {
      if (re.test(window)) { result.movement_type = type; break; }
    }
  }

  // ---- Sizing ----
  // Emmy pages quote both Ligne (e.g. "11.5'''") AND Diameter (e.g. "26 mm"),
  // and the user wants the Ligne value as the canonical `size` so it matches
  // how watchmakers actually describe a movement. Prefer Ligne; fall back
  // through the chain if it's missing. Ranfft uses `Lignes Dimensions` and
  // `Dimensions` headings — also accepted.
  const sizeRaw =
    map["lignes dimensions"] || map["ligne"] || map["lignes"] ||
    map["diameter"] || map["dimensions"] ||
    map["movement size"] || map["size"];
  if (sizeRaw) result.size = sizeRaw.replace(/\s+/g, " ").slice(0, 40);

  // ---- Jewels ----
  const jewels = map["jewels"] || map["stones"] || map["jewel count"];
  if (jewels) {
    // Keep compound forms like "17/21"; strip trailing words.
    const cleaned = jewels.replace(/\s+/g, " ").split(/[a-z(]/i)[0].trim();
    if (cleaned) result.jewels = cleaned;
  }

  // ---- Beat rate ----
  const bphRaw =
    map["frequency"] || map["vibrations"] || map["beat"] ||
    map["vph"] || map["bph"] || map["a/h"];
  if (bphRaw) {
    const n = toInt(bphRaw);
    if (n && n >= 3600 && n <= 100000) result.bph = n;
    if (!result.bph) {
      const hzMatch = bphRaw.match(/(\d(?:\.\d+)?)\s*Hz\b/i);
      if (hzMatch) result.bph = Math.round(parseFloat(hzMatch[1]) * 7200);
    }
  }
  if (!result.bph) {
    const hzMatch = body.match(/(\d(?:\.\d+)?)\s*Hz\b/i);
    if (hzMatch) {
      const v = Math.round(parseFloat(hzMatch[1]) * 7200);
      if (v >= 3600 && v <= 100000) result.bph = v;
    }
  }

  // ---- Lift angle ----
  const lift = map["lift angle"] || map["lift"];
  if (lift) {
    const n = toFloat(lift);
    if (n && n >= 30 && n <= 80) result.lift_angle = n;
  }

  // ---- Shock protection ----
  const protection =
    map["shock protection"] || map["shock system"] || map["antichoc"] ||
    map["protection"] || map["shock"];
  if (protection) result.protection = protection.replace(/\s+/g, " ").slice(0, 60);

  // ---- Year introduced / discontinued ----
  const yearIn =
    map["year introduced"] || map["introduced"] || map["production start"] ||
    map["launch year"] || map["launched"] || map["since"] ||
    map["period used"] || map["year"];
  if (yearIn) {
    const n = toInt(yearIn);
    if (n && n > 1800 && n < 2100) result.year_introduced = n;
    // "1982 - present"
    if (!result.year_discontinued) {
      const range = yearIn.match(/\b(\d{4})\s*[-–—]\s*(\d{4}|present|today)\b/i);
      if (range) {
        const lo = parseInt(range[1], 10);
        if (lo > 1800 && lo < 2100) result.year_introduced = lo;
        if (!/present|today/i.test(range[2])) {
          const hi = parseInt(range[2], 10);
          if (hi > 1800 && hi < 2100) result.year_discontinued = hi;
        }
      }
    }
  }
  const yearOut = map["discontinued"] || map["production end"] || map["until"];
  if (yearOut) {
    const n = toInt(yearOut);
    if (n && n > 1800 && n < 2100) result.year_discontinued = n;
  }

  // ---- Hand hole sizes ----
  // "1.50 x 0.90 x 0.20 mm" or "1.50 / 0.90 / 0.20" or "H/M/S 1.50 / 0.90 / 0.20"
  const handsLine = map["hands"] || map["hand sizes"];
  if (handsLine) {
    const m = handsLine.match(/(\d+(?:\.\d+)?)\s*[x\/]\s*(\d+(?:\.\d+)?)\s*[x\/]\s*(\d+(?:\.\d+)?)/i);
    if (m) {
      result.hour_hand_mm = parseFloat(m[1]);
      result.minute_hand_mm = parseFloat(m[2]);
      result.second_hand_mm = parseFloat(m[3]);
    }
  }
  if (result.hour_hand_mm === undefined) {
    const h = map["hour hand"]; const mn = map["minute hand"]; const s = map["second hand"];
    if (h) { const v = toFloat(h); if (v && v < 5) result.hour_hand_mm = v; }
    if (mn) { const v = toFloat(mn); if (v && v < 5) result.minute_hand_mm = v; }
    if (s) { const v = toFloat(s); if (v && v < 5) result.second_hand_mm = v; }
  }

  // ---- Source URL ----
  const srcLabel = map["source"] || map["url"];
  if (srcLabel) {
    const u = srcLabel.match(/https?:\/\/[^\s)\]]+/i);
    if (u) result.source_url = u[0];
  }
  if (!result.source_url) {
    const urlMatch = body.match(/https?:\/\/(?:www\.)?(?:ranfft\.(?:org|de)|emmywatch\.com)\/[^\s)\]]+/i);
    if (urlMatch) result.source_url = urlMatch[0];
  }

  const matched = Object.keys(result);
  return {
    parsed: result,
    matched,
    raw_preview: body.slice(0, 400),
  };
}

/**
 * Launchpad URL builders. Just search-page redirects that land the user on
 * the exact page they want to read.
 */
export const RANFFT_SEARCH = (caliber) =>
  `https://ranfft.org/caliber/index?CaliberSearch%5Bname%5D=${encodeURIComponent(caliber || "")}`;

export const EMMY_SEARCH = (caliber, mfg) => {
  const slug = (s) => (s || "").toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  if (mfg && caliber) {
    return `https://emmywatch.com/db/movement_/${slug(mfg)}--${slug(caliber)}/`;
  }
  return `https://emmywatch.com/search/?q=${encodeURIComponent(caliber || "")}`;
};
